import { ajv, EngagementTaskTrait } from '@cibo/core'
import { AutoFields, CiboAutoForm } from '@cibo/ui'
import AutoField from '@cibo/ui/src/components/Form/Uniforms/AutoField'
import { Stack } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import JSONSchemaBridge from 'uniforms-bridge-json-schema'
import { SubmitField } from 'uniforms-mui'
import { componentDetector } from './DataEntrySchemaEditingProvider/componentDetector'
import { PrefillEqipContractButton } from './PrefillEqipContractButton'

type SingleTargetSchemaEditorProps = {
  traitStatus: EngagementTaskTrait
  handleSaveTraitDetail: (detail: { value: any }) => void
  traitDetailValue: any
}

function createValidator(schema: object) {
  const validator = ajv.compile(schema)

  return (model: any) => {
    validator(model)
    return validator.errors?.length ? { details: validator.errors } : null
  }
}

const DisplayComponents: Record<any, any> = {
  Stack,
  PrefillEqipContractButton,
}

export const SingleTargetSchemaEditor = (props: SingleTargetSchemaEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')

  const [model, setModel] = useState<{ value: any }>()

  const { traitStatus, handleSaveTraitDetail, traitDetailValue } = props

  useEffect(() => {
    if (traitDetailValue) {
      setModel({ value: traitDetailValue })
    }
  }, [traitDetailValue])

  const bridge = useMemo<JSONSchemaBridge | undefined>(() => {
    if (!!traitStatus) {
      const formSchema = {
        type: 'object',
        properties: { value: { $id: 'schema', ...traitStatus?.schema } },
        required: ['value'],
      }

      return new JSONSchemaBridge({
        schema: formSchema,
        validator: createValidator(formSchema),
      })
    }

    return undefined
  }, [traitStatus?.schema])

  const handleSubmit = useCallback(
    (model: any) => {
      return handleSaveTraitDetail(model)
    },
    [handleSaveTraitDetail]
  )

  if (!bridge) {
    return null
  }

  const uiParams = traitStatus?.schema?.['ui-params'] || {}

  const renderDisplay = ({ field, itemProps }: { field: any; itemProps?: any }) => {
    if (typeof field === 'string') {
      return <AutoField key={field} name={field} {...itemProps} />
    }

    const Component = DisplayComponents[field.component]

    if (Component) {
      return (
        <Component key={field.name} {...itemProps} {...field.props}>
          {field.children?.map((child: any) => renderDisplay({ field: child }))}
        </Component>
      )
    }

    return <>{JSON.stringify(field)}</>
  }

  return (
    <AutoField.componentDetectorContext.Provider value={componentDetector}>
      <CiboAutoForm
        schema={bridge}
        model={model}
        onSubmit={handleSubmit}
        onChangeModel={setModel}
        validate="onChange"
        warningSchema={
          uiParams?.warningSchema && {
            type: 'object',
            properties: { value: uiParams?.warningSchema },
          }
        }
        renderDisplay={renderDisplay}
      >
        <AutoFields />

        <SubmitField label={t('save')} data-testid={`submit-button-${traitStatus?.traitId}`} />
      </CiboAutoForm>
    </AutoField.componentDetectorContext.Provider>
  )
}
